import { Link } from "@mui/material";
import { cn } from "@utils";
import {
  ExternalLink,
  FilePlus,
  FileText,
  KeyRound,
  MessageCircleQuestion,
  type LucideIcon,
} from "lucide-react";
import { useId, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { ChangeManagementRecommendation } from "src/types/change-management";
import {
  groupOperations,
  IndexedOperation,
  NEW_DOCUMENT_TILE,
  pluralize,
} from "./change-management-plan";
import { CollapseToggle } from "./CollapseToggle";
import { OperationCard } from "./OperationCard";
import { ReviewActionPlaceholders } from "./ReviewActionPlaceholders";
import { ResolvedPlanDocument } from "./useResolvedPlanDocuments";
import { VersionTileChip } from "./VersionTileChip";
import { ViewedCheckbox } from "./ViewedCheckbox";

type OperationState = { viewed: boolean; collapsed: boolean };

const SectionHeading = ({
  icon: Icon,
  label,
  count,
}: {
  icon: LucideIcon;
  label: string;
  count: string;
}) => (
  <h4 className="m-0 flex items-center gap-2 border-0 border-t border-solid border-black/[.12] bg-color-bg px-4 py-2 text-sm font-semibold leading-[1.5] text-black/[.87]">
    <Icon size={14} className="shrink-0 text-gray-500" />
    {label} <span className="text-sm font-normal text-black/60">{count}</span>
  </h4>
);

export const RecommendationCard = ({
  recommendation,
  resolvedDocument,
  getDataKeyLabel,
  getEvidenceLabel,
}: {
  recommendation: ChangeManagementRecommendation;
  resolvedDocument: ResolvedPlanDocument | null;
  getDataKeyLabel: (dataKey: string) => string | null;
  getEvidenceLabel: (sourceUrl: string) => string;
}) => {
  const { operations } = recommendation;
  const [operationStates, setOperationStates] = useState<OperationState[]>(() =>
    operations.map(() => ({ viewed: false, collapsed: false }))
  );
  const [collapsed, setCollapsed] = useState(false);
  const groups = groupOperations(operations);
  const isNewDocument = groups.create.length > 0;
  const tile = isNewDocument ? NEW_DOCUMENT_TILE : resolvedDocument?.tile;
  const viewedCount = operationStates.filter(({ viewed }) => viewed).length;
  const DocumentIcon = isNewDocument ? FilePlus : FileText;
  const titleId = useId();

  const setOperationViewed = (index: number, viewed: boolean) => {
    const next = operationStates.map((state, stateIndex) =>
      stateIndex === index ? { viewed, collapsed: viewed } : state
    );
    setOperationStates(next);
    setCollapsed(next.every((state) => state.viewed));
  };
  const setDocumentViewed = (viewed: boolean) => {
    setOperationStates(operations.map(() => ({ viewed, collapsed: viewed })));
    setCollapsed(viewed);
  };
  const toggleOperation = (index: number) =>
    setOperationStates(
      operationStates.map((state, stateIndex) =>
        stateIndex === index ? { ...state, collapsed: !state.collapsed } : state
      )
    );

  const renderOperations = (items: IndexedOperation[]) =>
    items.map(({ operation, index }) => (
      <OperationCard
        key={index}
        operation={operation}
        viewed={operationStates[index].viewed}
        collapsed={operationStates[index].collapsed}
        onViewedChange={(viewed) => setOperationViewed(index, viewed)}
        onToggle={() => toggleOperation(index)}
        getDataKeyLabel={getDataKeyLabel}
        getEvidenceLabel={getEvidenceLabel}
      />
    ));

  return (
    <section
      aria-labelledby={titleId}
      className="scroll-mt-4 rounded border border-solid border-black/[.12] bg-white"
    >
      <div
        className={cn(
          "flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3",
          collapsed && "opacity-85"
        )}
      >
        <div className="flex min-w-0 flex-[1_1_240px] flex-wrap items-center gap-x-2.5 gap-y-1">
          <DocumentIcon size={18} className="shrink-0 text-gray-500" />
          <h3
            id={titleId}
            className="m-0 min-w-0 text-[16px] font-semibold leading-[1.4] text-black/[.87]"
          >
            {resolvedDocument && !isNewDocument ? (
              <Link
                component={RouterLink}
                to={resolvedDocument.href}
                target="_blank"
                rel="noopener"
                underline="hover"
                color="inherit"
                className="inline-flex items-center gap-1.5"
              >
                {recommendation.document}
                <ExternalLink size={14} className="shrink-0 text-gray-500" />
              </Link>
            ) : (
              recommendation.document
            )}
          </h3>
          {tile && <VersionTileChip tile={tile} />}
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-0.5">
          {!collapsed && <ReviewActionPlaceholders showReject />}
          <ViewedCheckbox
            checked={viewedCount === operations.length}
            onChange={setDocumentViewed}
          >
            Viewed
            <span className="text-sm text-black/60">{`${viewedCount}/${operations.length}`}</span>
          </ViewedCheckbox>
          <CollapseToggle
            collapsed={collapsed}
            label="document"
            onToggle={() => setCollapsed(!collapsed)}
          />
        </div>
      </div>
      {!collapsed && (
        <>
          <p className="m-0 -mt-1 max-w-[78ch] pb-3 pl-[44px] pr-4 leading-[1.6] text-black/60">
            {recommendation.changes_summary}
          </p>
          {renderOperations(groups.create)}
          {groups.body.length > 0 && (
            <SectionHeading
              icon={FileText}
              label="Document body"
              count={pluralize(groups.body.length, "change")}
            />
          )}
          {renderOperations(groups.body)}
          {groups.dataKeys.length > 0 && (
            <SectionHeading
              icon={KeyRound}
              label="Data keys"
              count={pluralize(groups.dataKeys.length, "change")}
            />
          )}
          {renderOperations(groups.dataKeys)}
          {groups.reviews.length > 0 && (
            <SectionHeading
              icon={MessageCircleQuestion}
              label="Needs review"
              count={pluralize(groups.reviews.length, "question")}
            />
          )}
          {renderOperations(groups.reviews)}
        </>
      )}
    </section>
  );
};
